import { useMemo } from 'react'
import { Phone, UserX, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useTeamMembers, type TeamMember } from '@/api/agents'
import { useProfile } from '@/api/profile'
import { useCallActions } from '@/hooks/useCallActions'
import { useSelectedCallerId } from '@/hooks/useSelectedCallerId'
import { useUIStore } from '@/stores/useUIStore'

/**
 * Tab Équipe — liste des agents de la company avec statut temps réel
 * et bouton d'appel interne agent-à-agent (identité Twilio `client:agent_<id>_<company>`).
 */
export function TeamTab() {
  const { t } = useTranslation()
  const darkMode = useUIStore((s) => s.darkMode)
  const { data: profile } = useProfile()
  const { data: members = [], isLoading } = useTeamMembers()
  const { makeOutboundCall } = useCallActions()
  const selectedCallerId = useSelectedCallerId()

  const filteredMembers = useMemo(() => {
    if (!profile?.id) return members
    return members.filter((m: TeamMember) => m.user_id !== profile.id)
  }, [members, profile?.id])

  if (isLoading) {
    return (
      <div className={cn('h-full overflow-y-auto', darkMode ? 'bg-slate-950' : 'bg-white')}>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div
              className={cn(
                'h-8 w-32 rounded-lg animate-pulse',
                darkMode ? 'bg-slate-800' : 'bg-slate-200',
              )}
            />
            <div
              className={cn(
                'h-6 w-8 rounded-full animate-pulse',
                darkMode ? 'bg-slate-800' : 'bg-slate-200',
              )}
            />
          </div>
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className={cn(
                  'p-5 rounded-2xl border',
                  darkMode ? 'bg-slate-900/50 border-slate-800' : 'bg-white border-slate-200',
                )}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div
                    className={cn(
                      'w-12 h-12 rounded-full animate-pulse',
                      darkMode ? 'bg-slate-800' : 'bg-slate-200',
                    )}
                  />
                  <div className="flex-1 space-y-2">
                    <div
                      className={cn(
                        'h-4 w-24 rounded animate-pulse',
                        darkMode ? 'bg-slate-800' : 'bg-slate-200',
                      )}
                    />
                    <div
                      className={cn(
                        'h-3 w-16 rounded animate-pulse',
                        darkMode ? 'bg-slate-800' : 'bg-slate-200',
                      )}
                    />
                  </div>
                </div>
                <div
                  className={cn(
                    'h-7 w-36 rounded-full mb-4 animate-pulse',
                    darkMode ? 'bg-slate-800' : 'bg-slate-200',
                  )}
                />
                <div
                  className={cn(
                    'h-10 w-full rounded-lg animate-pulse',
                    darkMode ? 'bg-slate-800' : 'bg-slate-200',
                  )}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'h-full overflow-y-auto',
        darkMode ? 'bg-slate-950' : 'bg-slate-50/50',
      )}
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div
            className={cn(
              'p-2 rounded-xl',
              darkMode ? 'bg-blue-500/10' : 'bg-blue-50',
            )}
          >
            <Users
              className={cn('w-6 h-6', darkMode ? 'text-blue-400' : 'text-blue-600')}
            />
          </div>
          <div>
            <h2
              className={cn(
                'text-2xl font-bold',
                darkMode ? 'text-white' : 'text-slate-900',
              )}
            >
              {t('team.pageTitle', 'Équipe')}
            </h2>
            <p
              className={cn(
                'text-sm',
                darkMode ? 'text-slate-400' : 'text-slate-500',
              )}
            >
              {filteredMembers.length}{' '}
              {filteredMembers.length === 1
                ? t('team.memberSingular', 'membre')
                : t('team.memberPlural', 'membres')}
            </p>
          </div>
        </div>

        {filteredMembers.length === 0 ? (
          <div
            className={cn(
              'flex flex-col items-center justify-center py-16 px-8 rounded-2xl border-2 border-dashed',
              darkMode
                ? 'border-slate-800 bg-slate-900/30'
                : 'border-slate-200 bg-white',
            )}
          >
            <div
              className={cn(
                'p-4 rounded-2xl mb-4',
                darkMode ? 'bg-slate-800' : 'bg-slate-100',
              )}
            >
              <UserX
                className={cn(
                  'w-10 h-10',
                  darkMode ? 'text-slate-500' : 'text-slate-400',
                )}
              />
            </div>
            <h3
              className={cn(
                'text-lg font-semibold mb-2',
                darkMode ? 'text-slate-300' : 'text-slate-700',
              )}
            >
              {t('team.empty', 'Aucun membre')}
            </h3>
            <p
              className={cn(
                'text-sm text-center max-w-sm',
                darkMode ? 'text-slate-500' : 'text-slate-500',
              )}
            >
              {t(
                'team.emptyDesc',
                "Les membres de votre équipe apparaîtront ici une fois qu'ils auront rejoint.",
              )}
            </p>
          </div>
        ) : (
          <div className="grid gap-5 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {filteredMembers.map((member) => (
              <TeamMemberCard
                key={member.id}
                member={member}
                darkMode={darkMode}
                onCall={() =>
                  void makeOutboundCall(
                    `client:agent_${member.user_id}_${member.company}`,
                    selectedCallerId || undefined,
                    `${member.user_first_name || ''} ${member.user_last_name || ''}`.trim() ||
                      undefined,
                  )
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

interface CardProps {
  member: TeamMember
  darkMode: boolean
  onCall: () => void
}

function TeamMemberCard({ member, darkMode, onCall }: CardProps) {
  const { t } = useTranslation()
  // La disponibilité est pilotée par le DND (Ne pas déranger), pas par le status
  // ONLINE/OFFLINE : un membre est joignable tant qu'il n'a pas activé son DND.
  // BUSY (en appel) reste un état temps réel utile, prioritaire après le DND.
  const isDnd = member.dnd_enabled === true
  const isBusy = !isDnd && member.status === 'BUSY'
  const isAvailable = !isDnd && !isBusy

  return (
    <div
      className={cn(
        'p-5 rounded-2xl border transition-all duration-300 group',
        darkMode
          ? 'bg-slate-900/80 border-slate-800 hover:border-slate-600 hover:bg-slate-900 hover:shadow-2xl hover:shadow-slate-900/50'
          : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-xl hover:shadow-slate-200/50',
      )}
    >
      {/* Header — avatar + nom + rôle */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-shrink-0">
          <div
            className={cn(
              'w-12 h-12 rounded-full flex items-center justify-center text-base font-semibold transition-transform duration-300 group-hover:scale-105',
              darkMode
                ? 'bg-gradient-to-br from-slate-700 to-slate-800 text-slate-200 shadow-lg shadow-slate-900/50'
                : 'bg-gradient-to-br from-slate-100 to-slate-200 text-slate-700 shadow-md shadow-slate-200/50',
            )}
          >
            {member.user_first_name?.[0]}
            {member.user_last_name?.[0]}
          </div>
          <div
            className={cn(
              'absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full ring-2',
              darkMode ? 'ring-slate-900' : 'ring-white',
              isAvailable && 'bg-emerald-500 animate-pulse',
              isBusy && 'bg-amber-500',
              isDnd && 'bg-slate-400',
            )}
          />
        </div>

        <div className="min-w-0 flex-1">
          <h3
            className={cn(
              'font-semibold truncate text-[15px]',
              darkMode ? 'text-white' : 'text-slate-900',
            )}
          >
            {member.user_first_name} {member.user_last_name}
          </h3>
          <p
            className={cn(
              'text-xs',
              darkMode ? 'text-slate-400' : 'text-slate-500',
            )}
          >
            {member.role === 'MANAGER'
              ? t('team.manager', 'Manager')
              : t('team.agent', 'Agent')}
          </p>
        </div>
      </div>

      {/* Badge statut */}
      <div
        className={cn(
          'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-4 transition-all duration-300',
          isAvailable &&
            (darkMode
              ? 'bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30'
              : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'),
          isBusy &&
            (darkMode
              ? 'bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30'
              : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'),
          isDnd &&
            (darkMode
              ? 'bg-slate-500/15 text-slate-400 ring-1 ring-slate-500/30'
              : 'bg-slate-100 text-slate-500 ring-1 ring-slate-200'),
        )}
      >
        <span
          className={cn(
            'w-1.5 h-1.5 rounded-full',
            isAvailable && 'bg-emerald-500',
            isBusy && 'bg-amber-500',
            isDnd && 'bg-slate-400',
          )}
        />
        {isAvailable
          ? t('team.statusAvailable', 'Disponible')
          : isBusy
            ? t('team.statusBusy', 'Occupé')
            : t('team.statusUnavailable', 'Indisponible')}
      </div>

      {/* Bouton appel */}
      <button
        type="button"
        onClick={onCall}
        disabled={isDnd}
        className={cn(
          'w-full h-11 rounded-lg gap-2 flex items-center justify-center font-medium transition-all duration-300',
          isDnd
            ? darkMode
              ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
              : 'bg-slate-100 text-slate-400 cursor-not-allowed'
            : darkMode
              ? 'bg-blue-600 hover:bg-blue-500 hover:shadow-lg hover:shadow-blue-500/25 text-white'
              : 'bg-blue-600 hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-600/25 text-white',
        )}
      >
        <Phone className="w-4 h-4 mr-2" />
        {isDnd
          ? t('team.unavailable', 'Indisponible')
          : t('team.call', 'Appeler')}
      </button>
    </div>
  )
}
